/**
 * Age arithmetic shared by every surface that shows the age a birthday gives.
 *
 * Measured against the UTC calendar date, which is what decides the stored
 * value: Datetime::calculateAge compares in UTC. Measuring against the
 * browser's local date instead puts the two a day apart for anyone whose local
 * date differs from UTC, so the page would name an age the server will not
 * store.
 */

/** Whole years between the given date parts and today, on the UTC calendar. */
export function ageFromParts(day, month, year) {
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const birthDate = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));

    // Date.UTC maps a year of 0 to 99 onto 1900 to 1999. A typed year of 0002
    // would otherwise read as 1902 and give a plausible age.
    birthDate.setUTCFullYear(Number(year));

    let age = today.getUTCFullYear() - birthDate.getUTCFullYear();
    const monthDiff = today.getUTCMonth() - birthDate.getUTCMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getUTCDate() < birthDate.getUTCDate())) {
        age--;
    }

    return age;
}

/**
 * Whole years between a `Y-m-d` string and today, or null when the string is
 * not a usable date or the result falls outside a plausible human age.
 */
export function ageFromIsoDate(value) {
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
    if (!parts) {
        return null;
    }

    const year = Number(parts[1]);
    const month = Number(parts[2]);
    const day = Number(parts[3]);

    // Date.UTC rolls a day past the end of its month forward. The regex passes
    // 2026-02-30, which would otherwise read as March 1 and give an age.
    const parsed = new Date(Date.UTC(year, month - 1, day));
    parsed.setUTCFullYear(year);
    if (parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) {
        return null;
    }

    const age = ageFromParts(day, month, year);

    return age >= 0 && age < 130 ? age : null;
}
